"use client";

import { ArrowRight, BookOpen, Clock3, Star } from "lucide-react";
import { ProfileAvatar } from "../ui/profile-avatar";
import { CourseAvatarGroup } from "./people/course-avatar-group";
import { COURSE_AVATAR } from "./people/course-people.constants";
import {
  courseInitials,
  courseInstructors,
} from "./people/course-people.utils";
import type { CourseCardProps } from "./course-card.types";

/** The full-card action and instructor tooltips are siblings, avoiding nested buttons. */
export function CourseCard(props: CourseCardProps) {
  return (
    <article className="creator-job-card relative flex h-full flex-col rounded-[8px] border border-black bg-white p-2">
      <button
        type="button"
        onClick={props.onSelect}
        disabled={props.disabled}
        aria-label={props.accessibleLabel}
        className="absolute inset-0 z-10 rounded-[8px] outline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-wait disabled:opacity-60"
      />
      <div className="relative aspect-video w-full overflow-hidden rounded-[4px] bg-muted">
        <div
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center text-muted-foreground"
        >
          <BookOpen className="h-10 w-10" />
        </div>
        {props.imageUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={props.imageUrl}
            src={props.imageUrl}
            alt={props.imageAlt}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="relative h-full w-full object-cover"
            onError={(event) => {
              event.currentTarget.hidden = true;
            }}
          />
        )}
      </div>
      <div className="flex w-full flex-1 flex-col gap-2 px-1 pb-1 pt-3">
        <div className="flex min-w-0 items-center gap-2">
          {props.provider && (
            <>
              <ProfileAvatar
                src={props.providerLogoUrl}
                alt={props.provider}
                fallback={courseInitials(props.provider)}
                className={`h-9 w-9 text-xs ${COURSE_AVATAR.border}`}
                imageClassName={COURSE_AVATAR.logo}
                fallbackClassName={COURSE_AVATAR.fallback}
              />
              <p className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
                {props.provider}
              </p>
            </>
          )}
          {props.durationLabel && (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              <Clock3 aria-hidden="true" className="h-3.5 w-3.5" />
              {props.durationLabel}
            </span>
          )}
        </div>
        <h3 className="line-clamp-2 min-h-10 text-base font-semibold leading-5 text-foreground">
          {props.title}
        </h3>
        {props.description && (
          <p className="line-clamp-2 text-sm leading-5 text-muted-foreground">
            {props.description}
          </p>
        )}
        <div className="flex min-h-8 flex-wrap items-center justify-between gap-2">
          <CourseAvatarGroup
            instructors={courseInstructors(props.instructors, props.instructor)}
            label={props.instructorsLabel || props.instructor}
          />
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {props.ratingLabel && (
              <span className="inline-flex items-center gap-1">
                <Star
                  aria-hidden="true"
                  className="h-3.5 w-3.5 fill-amber-500 text-amber-500"
                />
                <span className="font-semibold text-foreground">
                  {props.ratingLabel}
                </span>
              </span>
            )}
            {props.reviewsLabel && <span>{props.reviewsLabel}</span>}
            {props.lectureLabel && (
              <span className="inline-flex items-center gap-1">
                <BookOpen aria-hidden="true" className="h-3.5 w-3.5" />
                {props.lectureLabel}
              </span>
            )}
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-3 text-xs">
          <span className="text-muted-foreground">{props.levelLabel}</span>
          <span className="inline-flex items-center gap-1 font-semibold text-primary">
            {props.actionLabel}
            <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </article>
  );
}
